import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  CalendarCheck,
  Clock,
  Users,
  LayoutList,
  Bell,
  ChevronRight,
  Sun,
  ArrowLeftRight,
} from 'lucide-react';
import { format, parseISO, endOfMonth } from 'date-fns';
import toast from 'react-hot-toast';

import api from '../api/axios';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        background: 'var(--white)',
        color: 'var(--text)',
        textDecoration: 'none',
        transition: 'var(--transition)',
        fontSize: '0.9rem',
        fontWeight: 500,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--primary-xlight)';
        e.currentTarget.style.borderColor = 'var(--primary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--white)';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Icon size={17} style={{ color: 'var(--primary-dark)' }} />
        {label}
      </div>
      <ChevronRight size={15} style={{ color: 'var(--text-muted)' }} />
    </Link>
  );
}

function DashboardPage() {
  const { user, isAdmin } = useAuth();

  const [nextShift, setNextShift] = useState(undefined); // undefined = loading, null = none
  const [periods, setPeriods] = useState([]);
  const [openCoversCount, setOpenCoversCount] = useState(0);
  const [monthStats, setMonthStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentMonth = format(new Date(), 'yyyy-MM');
    const monthStart = `${currentMonth}-01`;
    const monthEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');

    const fetches = [
      api.get('/shifts/my-shifts').catch(() => null),
      api.get('/shift-covers/open').catch(() => null),
      api.get(`/time-entries/me?start=${monthStart}&end=${monthEnd}`).catch(() => null),
      api.get(`/sunday-bonus/hours?month=${currentMonth}`).catch(() => null),
    ];
    if (isAdmin) fetches.push(api.get('/shifts/roster-periods').catch(() => null));

    Promise.all(fetches).then(([shiftsRes, coversRes, timeEntriesRes, bonusRes, periodsRes]) => {
      if (shiftsRes) {
        setNextShift(shiftsRes.data[0] ?? null);
      } else {
        setNextShift(null);
        toast.error('Could not load your shifts.');
      }
      if (coversRes) setOpenCoversCount(coversRes.data.length);
      if (periodsRes) setPeriods(periodsRes.data);
      if (timeEntriesRes) {
        const entries = timeEntriesRes.data;
        const regularMinutes = entries.filter((e) => e.entry_type !== 'sick_leave').reduce((s, e) => s + (e.total_minutes || 0), 0);
        const sickMinutes = entries.filter((e) => e.entry_type === 'sick_leave' && e.au_certificate_submitted).reduce((s, e) => s + (e.total_minutes || 0), 0);
        setMonthStats({ regularMinutes, sickMinutes, bonusMinutes: bonusRes?.data?.bonus_minutes ?? 0 });
      }
      setLoading(false);
    });
  }, [isAdmin]);

  const draftCount = periods.filter((p) => p.status === 'draft').length;
  const publishedCount = periods.filter((p) => p.status === 'published').length;
  const latestDraft = periods.find((p) => p.status === 'draft');

  const hourOfDay = new Date().getHours();
  const greeting =
    hourOfDay < 12 ? 'Good morning' : hourOfDay < 18 ? 'Good afternoon' : 'Good evening';

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <div>
      {/* Greeting */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Sun size={26} style={{ color: 'var(--primary-dark)' }} />
            {greeting}, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="page-subtitle">
            {new Date().toLocaleDateString('en-DE', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className={isAdmin ? 'grid-3' : 'grid-2'} style={{ gap: '16px', marginBottom: '24px' }}>
        {/* Next shift */}
        <div className="stat-card">
          <div className="stat-icon stat-icon-primary">
            <CalendarCheck size={22} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Next shift</p>
            {nextShift ? (
              <>
                <p className="stat-value" style={{ fontSize: '1.2rem' }}>
                  {format(parseISO(nextShift.date), 'EEE d MMM')}
                </p>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '2px' }}>
                  {nextShift.start_time.slice(0, 5)} – {nextShift.end_time.slice(0, 5)}
                  {nextShift.template_name && ` · ${nextShift.template_name}`}
                </p>
              </>
            ) : (
              <p className="stat-value" style={{ fontSize: '1rem', color: 'var(--text-muted)' }}>
                None scheduled
              </p>
            )}
          </div>
        </div>

        {/* Admin: Roster periods */}
        {isAdmin && (
          <div className="stat-card">
            <div className="stat-icon stat-icon-warning">
              <Calendar size={22} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Roster periods</p>
              <p className="stat-value" style={{ fontSize: '1.4rem' }}>
                {periods.length}
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '2px' }}>
                <span
                  className="badge badge-warning"
                  style={{ marginRight: '6px', fontSize: '0.7rem' }}
                >
                  {draftCount} draft
                </span>
                <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>
                  {publishedCount} published
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Monthly hours */}
        {monthStats && (monthStats.regularMinutes > 0 || monthStats.sickMinutes > 0) && (
          <div className="stat-card">
            <div className="stat-icon stat-icon-primary">
              <Clock size={22} />
            </div>
            <div className="stat-content">
              <p className="stat-label">This month</p>
              <p className="stat-value" style={{ fontSize: '1.2rem' }}>
                {Math.floor(monthStats.regularMinutes / 60)}h {monthStats.regularMinutes % 60}m
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '2px' }}>
                {monthStats.sickMinutes > 0 && `${Math.floor(monthStats.sickMinutes / 60)}h sick · `}
                {monthStats.bonusMinutes > 0 && `${Math.floor(monthStats.bonusMinutes / 60)}h bonus`}
              </p>
            </div>
          </div>
        )}

        {/* Open cover requests */}
        {openCoversCount > 0 && (
          <div className="stat-card">
            <div className="stat-icon stat-icon-danger">
              <ArrowLeftRight size={22} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Open cover requests</p>
              <p className="stat-value" style={{ fontSize: '1.4rem' }}>{openCoversCount}</p>
              <Link to="/roster" style={{ fontSize: '0.8rem', color: 'var(--primary-dark)' }}>
                View on roster →
              </Link>
            </div>
          </div>
        )}

        {/* Quick links card */}
        <div
          className="card"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            gridColumn: isAdmin ? undefined : undefined,
          }}
        >
          <p
            className="card-title"
            style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}
          >
            Quick links
          </p>
          <QuickLink to="/roster" icon={Calendar} label="Roster" />
          <QuickLink to="/my-shifts" icon={CalendarCheck} label="My Shifts" />
          {isAdmin && <QuickLink to="/users" icon={Users} label="Team" />}
          {isAdmin && <QuickLink to="/shift-templates" icon={LayoutList} label="Shift Templates" />}
          <QuickLink to="/notifications" icon={Bell} label="Notifications" />
        </div>
      </div>

      {/* Admin: draft roster alert */}
      {isAdmin && latestDraft && (
        <div
          style={{
            background: 'var(--warning-light)',
            border: '1px solid var(--warning)',
            borderRadius: 'var(--radius-lg)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Clock size={18} style={{ color: 'var(--warning)', flexShrink: 0 }} />
            <div>
              <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>Draft roster pending</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
                {latestDraft.start_date} – {latestDraft.end_date} has not been published yet.
              </p>
            </div>
          </div>
          <Link to="/roster" className="btn btn-primary btn-sm">
            <Calendar size={14} />
            View Roster
          </Link>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
